import { useEffect, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { isKeyExist } from 'commons/utils'
import { Request } from 'commons/utils/request'
import { CircularProgress, IconName, Page, PageFace, Panel } from 'ui/components'
import { useSecondaryMenu } from 'ui/components/BPLayout'

import { ConnectedIcon, NoConnectedIcon } from '../../styles'
import { BioTrackCollection, BreadCrumbs, DutchieCollection, MetrcCollection } from './components'
import { ConnectionsDetailsFormPayload, ConnectionsDetailsWebPage } from './types'

import * as SC from './styles'

const ConnectionsDetails = () => {
  const licenseId = getRouteApi('/_app/key-connections/$licenseId').useParams({ select: (s) => Number(s.licenseId) })

  const [webPage, setWebPage] = useState<ConnectionsDetailsWebPage>()
  const [payload, setPayload] = useState<ConnectionsDetailsFormPayload>(new ConnectionsDetailsFormPayload(licenseId))

  const { mutateAsync: getWebPage } = useMutation<ConnectionsDetailsWebPage, Error, ConnectionsDetailsFormPayload>({
    mutationFn: () => Request.post(Request.urls.bank.connectionsDetailsWebpages, payload),
    onSuccess: (result) => {
      setWebPage(result)
      setPayload(new ConnectionsDetailsFormPayload(licenseId, result.form))
    }
  })

  useSecondaryMenu([
    ...(isKeyExist(webPage, 'showMetrcCollection') ? [{ label: 'Metrc', hash: 'metrc' }] : []),
    ...(isKeyExist(webPage, 'showBioTrackCollection') ? [{ label: 'BioTrackTHC', hash: 'biotrack' }] : []),
    ...(isKeyExist(webPage, 'showDutchieCollection') ? [{ label: 'Dutchie POS', hash: 'dutchie' }] : [])
  ])

  useEffect(() => {
    getWebPage(payload)
  }, [])

  if (!webPage) {
    return <CircularProgress centered />
  }

  return (
    <Page
      noPadding
      face={PageFace.SECONDARY}
      title={
        <>
          {webPage.licenseName} ({webPage.licenseNumber})
        </>
      }
      subTitle="Connection Details"
      breadCrumbs={<BreadCrumbs licenseName={webPage.licenseName} licenseNumber={webPage.licenseNumber} />}
    >
      {isKeyExist(webPage, 'showMetrcCollection') && webPage.showMetrcCollection.length > 0 && (
        <Panel
          isArrowButton
          anchor="metrc"
          title="Metrc"
          actions={
            <SC.ConnectionInfoWrapper>
              {webPage.showMetrcCollectionConnected ? (
                <>
                  <ConnectedIcon name={IconName.CHECK_ROUND} /> Connected
                </>
              ) : (
                <>
                  <NoConnectedIcon name={IconName.CROSS_ROUND} /> Disconnected
                </>
              )}
            </SC.ConnectionInfoWrapper>
          }
        >
          {webPage.showMetrcCollection.map((collection, index) => (
            <MetrcCollection
              key={index}
              collection={collection}
              payload={payload}
              setPayload={setPayload}
              getWebPage={getWebPage}
            />
          ))}
        </Panel>
      )}

      {isKeyExist(webPage, 'showBioTrackCollection') && webPage.showBioTrackCollection.length > 0 && (
        <Panel
          isArrowButton
          anchor="biotrack"
          title="BioTrackTHC"
          actions={
            <SC.ConnectionInfoWrapper>
              {webPage.showBioTrackCollectionConnected ? (
                <>
                  <ConnectedIcon name={IconName.CHECK_ROUND} /> Connected
                </>
              ) : (
                <>
                  <NoConnectedIcon name={IconName.CROSS_ROUND} /> Disconnected
                </>
              )}
            </SC.ConnectionInfoWrapper>
          }
        >
          {webPage.showBioTrackCollection.map((collection, index) => (
            <BioTrackCollection
              key={index}
              collection={collection}
              payload={payload}
              setPayload={setPayload}
              getWebPage={getWebPage}
            />
          ))}
        </Panel>
      )}

      {isKeyExist(webPage, 'showDutchieCollection') && webPage.showDutchieCollection.length > 0 && (
        <Panel
          isArrowButton
          anchor="dutchie"
          title="Dutchie POS"
          actions={
            <SC.ConnectionInfoWrapper>
              {webPage.showDutchieCollectionConnected ? (
                <>
                  <ConnectedIcon name={IconName.CHECK_ROUND} /> Connected
                </>
              ) : (
                <>
                  <NoConnectedIcon name={IconName.CROSS_ROUND} /> Disconnected
                </>
              )}
            </SC.ConnectionInfoWrapper>
          }
        >
          {webPage.showDutchieCollection.map((collection, index) => (
            <DutchieCollection
              key={index}
              collection={collection}
              payload={payload}
              setPayload={setPayload}
              getWebPage={getWebPage}
            />
          ))}
        </Panel>
      )}
    </Page>
  )
}

export default ConnectionsDetails
